/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { CommonKeyCombinations } from "../../common/KeyCombinations";
import { IEditor } from "../../IEditor";
import { termsEditorViewEditor } from "../../terms/viewEditor";
import { ViewEditorView } from "./ViewEditorEdit/ViewEditorView";
import { CMD } from "../../commands/CMD";

export function registerKeyBindings(dc: IEditor): void {
	const viewSvc = dc.get("viewService");
	const keyBindingSvc = dc.get("keyBindingService");

	keyBindingSvc.register({
		id: "viewEditor.edit.toggleLeftPanel",
		commandId: "viewEditor.edit.toggleLeftPanel",
		label: termsEditorViewEditor.commands.toggleLeftPanel,
		keys: CommonKeyCombinations.toggleLeftPanel,
		when: () => viewSvc.isViewActive("viewEditor.edit")
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.toggleRightPanel",
		commandId: "viewEditor.edit.toggleRightPanel",
		label: termsEditorViewEditor.commands.toggleRightPanel,
		keys: CommonKeyCombinations.toggleRightPanel,
		when: () => viewSvc.isViewActive("viewEditor.edit")
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.hideAllPanels",
		commandId: "viewEditor.edit.hideAllPanels",
		label: termsEditorViewEditor.commands.hideAllPanels,
		keys: CommonKeyCombinations.hideAllPanels,
		when: () => viewSvc.isViewActive("viewEditor.edit")
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.toggleConsole",
		commandId: "viewEditor.edit.toggleConsole",
		label: termsEditorViewEditor.commands.toggleConsole,
		keys: CommonKeyCombinations.toggleConsole,
		when: () => viewSvc.isViewActive("viewEditor.edit")
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.saveResource",
		label: termsEditorViewEditor.commands.saveResource,
		commandId: "viewEditor.edit.saveResource",
		keys: CommonKeyCombinations.save,
		when: () => viewSvc.isViewActive("viewEditor.edit")
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.deleteSelection",
		label: termsEditorViewEditor.commands.deleteSelection,
		commandId: "viewEditor.edit.deleteSelection",
		keys: CommonKeyCombinations.delete,
		when: () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				const viewEditor = viewSvc.getCurrentView() as ViewEditorView;
				return viewEditor.isLayoutPanelActive([ "outline", "content" ]);
			}
		}
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.unselectAll",
		label: termsEditorViewEditor.commands.unselectAll,
		commandId: "viewEditor.edit.unselectAll",
		keys: CommonKeyCombinations.escape,
		when: () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				const viewEditor = viewSvc.getCurrentView() as ViewEditorView;
				return viewEditor.isLayoutPanelActive([ "outline", "content", "eventFlow" ]);
			}
		}
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.copy",
		label: termsEditorViewEditor.commands.unselectAll,
		commandId: CMD.VIEW_EDITOR.EDIT.COPY,
		keys: CommonKeyCombinations.escape,
		when: () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				const viewEditor = viewSvc.getCurrentView() as ViewEditorView;
				return viewEditor.isLayoutPanelActive([ "outline", "content", "eventFlow" ]);
			}
		}
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.undo",
		label: termsEditorViewEditor.commands.unselectAll,
		commandId: CMD.VIEW_EDITOR.EDIT.UNDO,
		keys: CommonKeyCombinations.undo,
		when: () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				return viewSvc.hasModifiedViews();
			}
		}
	});

	keyBindingSvc.register({
		id: "viewEditor.edit.redo",
		label: termsEditorViewEditor.commands.unselectAll,
		commandId: CMD.VIEW_EDITOR.EDIT.REDO,
		keys: CommonKeyCombinations.redo,
		when: () => {
			if (viewSvc.isViewActive("viewEditor.edit")) {
				return viewSvc.hasModifiedViews();
			}
		}
	});
}
